import { createFileRoute, redirect, notFound } from "@tanstack/react-router";
import { useParams, Navigate } from "@/lib/router-compat";
import { findVideoBySlug, VIDEOS } from "@/pages/videos/videoData";
import { VideoWatchPage } from "@/pages/videos/VideoWatchPage";
import { SITE_URL, buildHreflang } from "@/lib/i18n/locale";

export const Route = createFileRoute("/videos/$slug")({
  beforeLoad: ({ params }) => {
    if (!findVideoBySlug(params.slug)) {
      throw notFound();
    }
  },
  component: RouteComponent,
  head({ params }) {
    const video = findVideoBySlug(params.slug);
    if (!video) {
      return {
        meta: [
          { title: "Video not found" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const url = `${SITE_URL}/videos/${video.slug}`;
    const title = `${video.title} | Neuro Wellness TMS`;
    return {
      meta: [
        { title },
        { name: "description", content: video.description },
        { property: "og:title", content: title },
        { property: "og:description", content: video.description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        {
          property: "og:image",
          content: `https://i.ytimg.com/vi/${video.id}/maxresdefault.jpg`,
        },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: video.description },
        {
          name: "twitter:image",
          content: `https://i.ytimg.com/vi/${video.id}/maxresdefault.jpg`,
        },
      ],
      links: [
        { rel: "canonical", href: url },
        ...buildHreflang(`/videos/${video.slug}`),
      ],
    };
  },
});

function RouteComponent() {
  const { slug } = useParams<{ slug: string }>();
  const video = findVideoBySlug(slug);
  if (!video) {
    return <Navigate to="/videos" replace />;
  }
  return <VideoWatchPage video={video} />;
}

// Preload all video slugs so the route type-checks and the build knows the
// full set of indexable watch pages.
export const _VIDEO_SLUGS = VIDEOS.map((v) => v.slug);
void redirect;
